import { useEffect, useMemo, useState } from 'react'

import {
  DoProduct,
  MenuItem,
  filterItem,
  filterProduct,
  sortCatalogEntries
} from 'public/components/default_template/filter_and_sort/FilterMatcher'
import {
  useOOMenus,
  Props as OOMenusProps
} from 'public/components/online_ordering/useOOMenu'
import { useFlags } from 'shared/components/common/feature_flags/useFlags'

const enum CatalogMode {
  /** Initial fetch in flight — mode not yet decided. */
  Fetching = 'fetching',
  /** Full unfiltered product catalog is in memory — all filtering/sorting is handled in-browser. */
  Local = 'local',
  /** Catalog is too large or filters exist — filterCriteria passes through to Apollo. */
  Server = 'server'
}

type Baseline = {
  items: MenuItem[]
  products: DoProduct[]
  totalCount: number
}

type Props = Omit<OOMenusProps, 'skip' | 'skipQuery' | 'skipPopularItems'>

/**
 * Wrapper around useOOMenus for the EcommPage that decides — once per URL
 * (categorySlug/channelGuid/etc.) — whether the catalog is small enough to
 * filter entirely in the browser.
 *
 * Strategy:
 *   1. Fetch with the real filterCriteria (null on first load, since filters
 *      are always cleared on navigation for now).
 *   2. If hasMore=false AND filterCriteria=null, the client holds the full
 *      unfiltered catalog. Lock into local mode — all subsequent filter/sort
 *      changes are applied in-memory; no further server requests until inputs change
 *   3. If hasMore=true OR filterCriteria is set, use server mode — pass
 *      filterCriteria through to Apollo as before. Re-evaluated after each
 *      fetchMore: once pagination completes (hasMore=false) with no active
 *      filters, the baseline is captured and mode transitions to Local.
 */
export const useEcommMenus = (props: Props) => {
  const { filterCriteria, categorySlug, channelGuid } = props
  const { ooEcommFilterAndSort } = useFlags()

  const [mode, setMode] = useState<CatalogMode>(CatalogMode.Fetching)
  const [baseline, setBaseline] = useState<Baseline | null>(null)

  // Reset when the category changes — the component stays mounted across category navigation.
  useEffect(() => {
    setMode(CatalogMode.Fetching)
    setBaseline(null)
  }, [categorySlug, channelGuid]) // eslint-disable-line react-hooks/exhaustive-deps

  const result = useOOMenus({
    ...props,
    skipPopularItems: true,
    filterCriteria,
    skipQuery: ooEcommFilterAndSort && mode === CatalogMode.Local
  })

  const { sort, setSort } = result

  // Once a request is made without filters, decide if we can filter in memory or rely on do-menus
  useEffect(() => {
    if (
      mode === CatalogMode.Local ||
      result.loading ||
      (result.menuItems == null && result.products == null)
    ) {
      return
    }

    if (filterCriteria != null) {
      // Fetched a subset — can't use as baseline.
      if (mode !== CatalogMode.Server) {
        setMode(CatalogMode.Server)
      }
      return
    }

    if (result.hasMore) {
      // Still paginating — re-evaluate when fetchMore completes and hasMore drops to false.
      if (mode !== CatalogMode.Server) {
        setMode(CatalogMode.Server)
      }
      return
    }

    const products = (result.products ?? []) as DoProduct[]
    const variantGuids = new Set(
      products.flatMap((p) => (p.variants ?? []).map((v) => v.guid))
    )
    // menuItems includes both standalone items and flattened product variants.
    // Strip the variants so the baseline only holds standalone items alongside
    // the DoProduct[] which already carry variant data for filterProduct.
    const items = ((result.menuItems ?? []) as MenuItem[]).filter(
      (i) => !variantGuids.has(i.guid)
    )

    setMode(CatalogMode.Local)
    setBaseline({
      items,
      products,
      totalCount: result.totalCount ?? 0
    })
  }, [
    mode,
    filterCriteria,
    result.loading,
    result.hasMore,
    result.menuItems,
    result.products,
    result.totalCount
  ])

  const filteredItems = useMemo(() => {
    if (!baseline) {
      return null
    }
    return sortCatalogEntries(
      baseline.items.filter((i) => filterItem(i, filterCriteria ?? {}, false)),
      sort
    )
  }, [baseline, filterCriteria, sort])

  const filteredProducts = useMemo(() => {
    if (!baseline) {
      return null
    }
    return sortCatalogEntries(
      baseline.products.filter((p) =>
        filterProduct(p, filterCriteria ?? {}, false)
      ),
      sort
    )
  }, [baseline, filterCriteria, sort])

  const localMenuItems = useMemo(() => {
    if (filteredItems == null && filteredProducts == null) {
      return null
    }
    const variants = filteredProducts?.flatMap((p) => p.variants ?? []) ?? []
    return [...(filteredItems ?? []), ...variants]
  }, [filteredItems, filteredProducts])

  if (
    ooEcommFilterAndSort &&
    mode === CatalogMode.Local &&
    filteredItems != null &&
    filteredProducts != null
  ) {
    const isFiltered = !!(filterCriteria || sort)
    return {
      ...result,
      menuItems: isFiltered ? localMenuItems ?? undefined : result.menuItems,
      products: isFiltered ? filteredProducts : result.products,
      // do-menus will return items or products, never mixed
      totalCount: isFiltered
        ? filteredProducts.length > 0
          ? filteredProducts.length
          : filteredItems.length
        : baseline!.totalCount,
      hasMore: false,
      loading: false,
      loadingNewFilterData: false,
      fetchMore: (_offset: number) => {},
      sort,
      setSort
    }
  }

  return result
}
